import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getSpecifications } from '../../../models/specificationModel';
import { toast } from 'react-toastify';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import DebugModal from '../../pages/Requisitions/modals/DebugModal';

// Debounced Input for Search
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce]); // Removed onChange from dependencies to prevent infinite loop

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

function ESRSpecificationsList() {
  const [specifications, setSpecifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPagination = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 20;
    return { pageIndex: page - 1, pageSize };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugSpec, setDebugSpec] = useState(null);
  // Load view mode from localStorage, default to 'grid'
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('esrSpecificationsViewMode');
    return savedViewMode || 'grid';
  });

  // Save view mode to localStorage whenever it changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('esrSpecificationsViewMode', mode);
  };

  const fetchSpecifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      params.append('type', 'emergency'); // Fixed type for ESR
      if (globalFilter) {
        params.append('filter', globalFilter);
      }
      if (statusFilter) {
        params.append('requisition_status', statusFilter);
      }
      if (sorting.length > 0) {
        params.append('sortBy', sorting[0].id);
        params.append('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      }
       
      const response = await getSpecifications(params);
      
      if (response.status === 1 && response.data && response.data.data && response.data.meta) {
        // Process specification data
        if (response.data.data.length > 0) {
          // Process supplier data
          response.data.data.forEach(spec => {
            // Preserve original supplier data format for debugging
            if (typeof spec.specification_supplier === 'string' && spec.specification_supplier.startsWith('a:')) {
              // This is a PHP serialized array, preserve it for debugging
              spec._raw_supplier_data = spec.specification_supplier;
              
              try {
                // Try to parse it in a way that doesn't lose information
                const supplierMatches = spec.specification_supplier.match(/s:\d+:"([^"]+)"/g);
                if (supplierMatches) {
                  const suppliers = supplierMatches.map(match => {
                    const content = match.match(/s:\d+:"([^"]+)"/);
                    return content ? content[1] : null;
                  }).filter(Boolean);
                  
                  spec.specification_supplier = suppliers;
                }
              } catch (err) {
                // Silently handle error
              }
            }
          });
          
          response.data.data.forEach(spec => {
            // Check if this is a newly created specification (possibly missing fields)
            const isMissingDepartment = spec.department === undefined || spec.department === null || spec.department === 0;
            const isMissingRequisitionStatus = spec.requisition_status === undefined || spec.requisition_status === null;
            
            // Silently handle missing fields
          });
        }
        
        setSpecifications(response.data.data);
        setRowCount(response.data.meta.totalItems);
      } else {
        toast.error(response.message || 'Failed to load ESR specifications');
        setSpecifications([]);
        setRowCount(0);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while loading ESR specifications');
      setSpecifications([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter, sorting, statusFilter]);

  const handleRefresh = useCallback(() => {
    fetchSpecifications();
  }, [fetchSpecifications]);

  useEffect(() => {
    fetchSpecifications();
  }, [fetchSpecifications]);

  useEffect(() => {
    // Update the URL when pagination or filters change
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', pagination.pageIndex + 1);
    searchParams.set('pageSize', pagination.pageSize);
    
    if (globalFilter) {
      searchParams.set('filter', globalFilter);
    } else {
      searchParams.delete('filter');
    }
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  }, [pagination, globalFilter, navigate, location.pathname]); // Removed location.search to prevent infinite loop

  // Handle pagination change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, pageIndex: newPage }));
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (e) => {
    const pageSize = Number(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0, pageSize }));
  };

  // Build pagination controls
  const totalPages = Math.ceil(rowCount / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  const paginationControls = (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6">
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Items per page:</span>
          <select
            value={pagination.pageSize}
            onChange={handleEntriesPerPageChange}
            className="block w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={40}>40</option>
          <option value={60}>60</option>
          <option value={100}>100</option>
        </select>
        <span className="text-sm text-gray-600">entries per page</span>
      </div>

      <span className="flex items-center gap-1 text-sm text-gray-600">
        Showing {Math.min(rowCount, pagination.pageIndex * pagination.pageSize + 1)} to {Math.min(rowCount, (pagination.pageIndex + 1) * pagination.pageSize)} of {rowCount} entries
      </span>

      <div className="flex items-center gap-2">
        <button
          onClick={() => handlePageChange(0)}
          disabled={currentPage === 1}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => handlePageChange(pagination.pageIndex - 1)}
          disabled={currentPage === 1}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <span className="flex items-center gap-1 text-sm text-gray-600 px-4">
          Page{' '}
          <strong>
            {pagination.pageIndex + 1} of{' '}{totalPages}
          </strong>
        </span>
        
        <button
          onClick={() => handlePageChange(pagination.pageIndex + 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Professional Header Card */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">ESR Specifications</h1>
                <p className="text-xs text-gray-500 mb-1">Emergency & Service Rendered</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                    {(globalFilter || statusFilter) && (
                      <span className="ml-1 text-blue-600">(filtered)</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex-shrink-0 inline-flex rounded-md shadow-sm border border-gray-300 bg-white" role="group">
                  <button
                    onClick={() => handleViewModeChange('grid')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-blue-50 text-blue-700 border-r border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 border-r border-gray-300'
                    }`}
                    title="Grid View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleViewModeChange('table')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-r-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'table'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Table View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                {/* Status Filter */}
                <div className="flex-shrink-0">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      statusFilter ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                    }`}
                  >
                    <option value="">All Statuses</option>
                    <option value="0">Pending</option>
                    <option value="1">Approved</option>
                    <option value="4">Rejected</option>
                  </select>
                </div>

                {/* Page Size Filter */}
                <div className="flex-shrink-0">
                  <select
                    value={pagination.pageSize}
                    onChange={(e) => {
                      setPagination(prev => ({
                        ...prev,
                        pageSize: Number(e.target.value),
                        pageIndex: 0
                      }));
                    }}
                    className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 25, 50, 100].map(size => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </select>
                </div>

                {/* Navigation Links */}
                <button
                  onClick={() => navigate('/dashboard/requisitions')}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  View Requisitions
                </button>
   

                {/* Create Button */}
                {can("Create Specification") && (
                  <Link
                    to="/dashboard/specifications/create-esr"
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                    style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0087D4' }}
                  >
                    <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create ESR Specification
                  </Link>
                )}
              </div>
              
              {/* Row 2: Search Box */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={value => {
                    setGlobalFilter(String(value));
                    setPagination(p => ({ ...p, pageIndex: 0 })); // Reset to first page when searching
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search specifications..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reduce spacer */}
      <div className="mt-1"></div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 mt-1">
      
      {/* ESR Specifications Content */}
      {loading ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading specifications...</span>
          </div>
        </div>
      ) : specifications.length === 0 ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <p className="text-gray-500">No specifications found.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {specifications.map((spec) => {
            const supplierCount = Array.isArray(spec.specification_supplier) ? spec.specification_supplier.length : 0;
            const selectedSupplier = spec.quotation_company_name;
            const glCode = spec.specification_workflow;
            const glDescription = spec.gl_description;
            
            const requisitionCheck = spec.requisition_status === undefined || spec.requisition_status === null || 
                                     spec.requisition_status < spec.specification_approvals;
            
            return (
              <div key={spec.specification_id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow hover:border-blue-200 transition-all duration-200 group flex flex-col h-full">
                {/* Header with ESR and GL Code inline */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      {spec.requisition_number ? `EPR-${spec.requisition_number}` : `ESR-${spec.specification_id}`}
                    </span>
                    {glCode && (
                      <span className="text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                        {glCode}
                      </span>
                    )}
                  </div>
                </div>

                {/* Title */}
                <Link
                  to={`/dashboard/specifications/${spec.specification_id}`}
                  className="block"
                >
                  <h3 className="font-semibold text-sm text-gray-900 hover:text-blue-600 transition-colors duration-200 mb-2 line-clamp-2 min-h-[2rem]">
                    {spec.specification_title}
                  </h3>
                </Link>

                {/* Flexible content area */}
                <div className="flex-1 mb-2">
                  {/* Selected Supplier - Compact */}
                  {selectedSupplier && (
                    <div className="mb-1.5 p-1.5 bg-green-50 border border-green-200 rounded text-xs">
                      <div className="flex items-center">
                        <svg className="w-2.5 h-2.5 text-green-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-xs font-semibold text-green-800 truncate">{selectedSupplier}</span>
                      </div>
                    </div>
                  )}

                  {/* Compact Metadata */}
                  <div className="space-y-0.5 text-xs text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="truncate font-medium">{spec.user_full_name || 'Unknown'}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9m0 0h6m-6 0V5a2 2 0 012-2h4a2 2 0 012 2v4" />
                      </svg>
                      <span className="truncate">{spec.department_name || 'General'}</span>
                    </div>

                    <div className="flex items-center">
                      <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>
                        {spec.specification_date && Number.isInteger(spec.specification_date) 
                          ? new Date(spec.specification_date * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                          : spec.specification_date || 'N/A'}
                      </span>
                      <span className={`ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        supplierCount > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {supplierCount} Supplier{supplierCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1 pt-2 border-t border-gray-100 mt-auto">
                  <Link
                    to={`/dashboard/specifications/${spec.specification_id}`}
                    className="flex-1 inline-flex items-center justify-center px-2 py-1.5 text-white text-xs font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                    style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' }}
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                    </svg>
                    View
                  </Link>
                  
                  {/* Edit button with more specific conditions matching the original PHP code */}
                  {((spec.specification_user === user?.user_id || 
                    user?.is_procurement === true || 
                    (spec.department && spec.department === user?.department)) && 
                    (spec.requisition_status === undefined || spec.requisition_status === null || 
                     spec.requisition_status < spec.specification_approvals)) && (
                    <Link
                      to={`/dashboard/specifications/${spec.specification_id}/edit`}
                      className="inline-flex items-center justify-center px-2 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                      title="Edit"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                      </svg>
                      Edit
                    </Link>
                  )}
                  
                  {/* Quote/Requisition button with more detailed debugging */}
                  {(() => {
                    // Logic for quotation display using original PHP conditions
                    const isCreator = spec.specification_user === user?.user_id;
                    const isSameDepartment = spec.department === user?.department;
                    const isAdmin = user?.user_id === 1;
                    const hasSuppliers = Array.isArray(spec.specification_supplier) && spec.specification_supplier.length > 0;
                    const statusCheck = spec.requisition_status === undefined || 
                                        spec.requisition_status === null || 
                                        spec.requisition_status < spec.specification_approvals;
                    
                    // Combined condition matching the PHP logic
                    const shouldShowQuotation = ((isCreator || isSameDepartment || isAdmin) && hasSuppliers) && statusCheck;
                    
                    // Only show if it matches the PHP logic
                    return  spec.requisition_number ? (
                      <Link
                        to={`/dashboard/requisitions/${spec.requisition_id}`}
                        className="inline-flex items-center justify-center px-2 py-1.5 border border-green-300 bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                        title="View Requisition"
                      >
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        Requisition
                      </Link>
                    ) : null;
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => {
                      const newSorting = sorting.length > 0 && sorting[0].id === 'specification_id'
                        ? [{ id: 'specification_id', desc: !sorting[0].desc }]
                        : [{ id: 'specification_id', desc: false }];
                      setSorting(newSorting);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Reference / GL
                      {sorting.length > 0 && sorting[0].id === 'specification_id' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          {sorting[0].desc ? (
                            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => {
                      const newSorting = sorting.length > 0 && sorting[0].id === 'specification_title'
                        ? [{ id: 'specification_title', desc: !sorting[0].desc }]
                        : [{ id: 'specification_title', desc: false }];
                      setSorting(newSorting);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      {sorting.length > 0 && sorting[0].id === 'specification_title' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          {sorting[0].desc ? (
                            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => {
                      const newSorting = sorting.length > 0 && sorting[0].id === 'user_full_name'
                        ? [{ id: 'user_full_name', desc: !sorting[0].desc }]
                        : [{ id: 'user_full_name', desc: false }];
                      setSorting(newSorting);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      User / Dept
                      {sorting.length > 0 && sorting[0].id === 'user_full_name' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          {sorting[0].desc ? (
                            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => {
                      const newSorting = sorting.length > 0 && sorting[0].id === 'specification_date'
                        ? [{ id: 'specification_date', desc: !sorting[0].desc }]
                        : [{ id: 'specification_date', desc: true }];
                      setSorting(newSorting);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Date
                      {sorting.length > 0 && sorting[0].id === 'specification_date' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          {sorting[0].desc ? (
                            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    # Suppliers
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => {
                      const newSorting = sorting.length > 0 && sorting[0].id === 'quotation_company_name'
                        ? [{ id: 'quotation_company_name', desc: !sorting[0].desc }]
                        : [{ id: 'quotation_company_name', desc: false }];
                      setSorting(newSorting);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Selected Quote
                      {sorting.length > 0 && sorting[0].id === 'quotation_company_name' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          {sorting[0].desc ? (
                            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {specifications.map((spec) => {
                  const supplierCount = Array.isArray(spec.specification_supplier) ? spec.specification_supplier.length : 0;
                  const selectedSupplier = spec.quotation_company_name;
                  const glCode = spec.specification_workflow;
                  
                  return (
                    <tr key={spec.specification_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded inline-block">
                              {spec.requisition_number ? `EPR-${spec.requisition_number}` : `ESR-${spec.specification_id}`}
                            </span>
                            {glCode && (
                              <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded inline-block">
                                {glCode}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Link
                              to={`/dashboard/specifications/${spec.specification_id}`}
                              className="inline-flex items-center px-2 py-1 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded transition-colors"
                              title="View"
                            >
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                              </svg>
                              View
                            </Link>
                            
                            {((spec.specification_user === user?.user_id || 
                              user?.is_procurement === true || 
                              (spec.department && spec.department === user?.department)) && 
                              (spec.requisition_status === undefined || spec.requisition_status === null || 
                               spec.requisition_status < spec.specification_approvals)) && (
                              <Link
                                to={`/dashboard/specifications/${spec.specification_id}/edit`}
                                className="inline-flex items-center px-2 py-1 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded transition-colors"
                                title="Edit"
                              >
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                </svg>
                                Edit
                              </Link>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/dashboard/specifications/${spec.specification_id}`}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2"
                        >
                          {spec.specification_title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">{spec.user_full_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{spec.department_name || 'General'}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-700">
                          {spec.specification_date && Number.isInteger(spec.specification_date) 
                            ? new Date(spec.specification_date * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : spec.specification_date || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          supplierCount > 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {supplierCount} Supplier{supplierCount !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {selectedSupplier ? (
                          <div className="flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-900">{selectedSupplier}</span>
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                            None
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {paginationControls}
    </div>
    
    {/* Debug Modal */}
    <DebugModal
      isOpen={showDebugModal}
      onClose={() => {
        setShowDebugModal(false);
        setDebugSpec(null);
      }}
      data={{
        user: user,
        // Put specifications directly at top level so it's visible in the debug modal
        requisition: debugSpec || { 
          type: "specifications_list",
          title: "ESR Specifications List Data",
          items: specifications,
          count: specifications.length,
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalItems: rowCount
        },
        selectedQuote: null, // Not applicable for specifications
        // Additional debugging data 
        debug: {
          userDepartment: user?.department,
          isProcurement: user?.is_procurement === true,
          userIsSuperUser: user?.user_id === 1,
          // Individual specification data if clicked from card
          selectedSpec: debugSpec ? {
            rawSpecification: debugSpec,
            supplierData: debugSpec.specification_supplier,
            supplierCount: Array.isArray(debugSpec.specification_supplier) ? debugSpec.specification_supplier.length : 0,
            specUser: debugSpec.specification_user,
            specDepartment: debugSpec.department,
            requisitionStatus: debugSpec.requisition_status,
            specificationApprovals: debugSpec.specification_approvals,
            // Quotation condition details (PHP matching)
            quotationCondition: {
              condition1: debugSpec.specification_user === user?.user_id,
              condition2: debugSpec.department === user?.department,
              condition3: user?.user_id === 1,
              hasSuppliers: Array.isArray(debugSpec.specification_supplier) && debugSpec.specification_supplier.length > 0,
              requisitionStatus: debugSpec.requisition_status,
              approvals: debugSpec.specification_approvals,
              statusLessThanApprovals: (debugSpec.requisition_status === undefined || 
                                       debugSpec.requisition_status === null || 
                                       debugSpec.requisition_status < debugSpec.specification_approvals)
            }
          } : null
        }
      }}
    />
    </div>
  );
}

export default ESRSpecificationsList;
